import { Fragment } from 'react';
import { Dialog, Button, Divider, CopyToClipboard } from '@schibsted-svp/react-ui';
import classnames from 'classnames/bind';
import { relative as getRelativeDate } from 'lib/date';
import {
    FieldContainer,
    createIsFieldChanged,
    formatAgeLimit,
    formatEditedDate,
    getFieldsChangesText,
    getMetadataChanges,
} from 'components/core/History';
import type { CategoryHistoryData } from '../types';
import {
    createIsMetadataKeyChanged,
    formatBoolean,
    formatVisibility,
    getAdditionalImages,
    getMetadataEntries,
} from './helpers';

import css from './HistoryPreview.module.scss';

const cln = classnames.bind(css);

type HistoryPreviewProps = {
    data: CategoryHistoryData;
    isDialogOpen: boolean;
    closeDialog: () => void;
};

export function HistoryPreview({ data, isDialogOpen, closeDialog }: HistoryPreviewProps) {
    const { date, allChanges, changes, categoryData } = data;
    const { additional } = categoryData;

    const isFieldChanged = createIsFieldChanged(changes);
    const isMetadataKeyChanged = createIsMetadataKeyChanged(getMetadataChanges(allChanges));

    const metadata = getMetadataEntries(categoryData);
    const images = getAdditionalImages(categoryData);

    return (
        <Dialog
            className={css.dialog}
            isOpen={isDialogOpen}
            heading={
                <>
                    History preview
                    <span className={css.headingInfo}>
                        <span className={css.date}>{date ? getRelativeDate(date * 1000) : 'Unknown date'}</span> -{' '}
                        {getFieldsChangesText(changes)}
                    </span>
                </>
            }
            headingClassName={css.heading}
            onClose={closeDialog}
        >
            <Dialog.Section className={css.container}>
                <div className={css.main}>
                    <FieldContainer label="Title" highlighted={isFieldChanged('title')} copyable>
                        {categoryData.title}
                    </FieldContainer>
                    <FieldContainer label="Short path" highlighted={isFieldChanged('additional.shortPath')} copyable>
                        {additional.shortPath}
                    </FieldContainer>
                    <FieldContainer
                        label="Description"
                        fieldClassName={css.description}
                        highlighted={isFieldChanged('additional.description')}
                        copyable
                    >
                        {additional.description}
                    </FieldContainer>
                    <Divider margin="0" />

                    <FieldContainer label="Parent category" highlighted={isFieldChanged('parentId')}>
                        {categoryData.parentId ? `ID: ${categoryData.parentId}` : undefined}
                    </FieldContainer>
                    <FieldContainer label="Visibility" highlighted={isFieldChanged('showCategory')}>
                        {formatVisibility(categoryData.showCategory)}
                    </FieldContainer>
                    <FieldContainer label="Series" highlighted={isFieldChanged('isSeries')}>
                        {formatBoolean(categoryData.isSeries)}
                    </FieldContainer>
                    <FieldContainer label="Age limit" highlighted={isFieldChanged('ageLimit')}>
                        {formatAgeLimit(categoryData.ageLimit)}
                    </FieldContainer>

                    <Divider margin="0" />

                    <FieldContainer
                        label="Metadata"
                        fieldClassName={css.metadata}
                        highlighted={isFieldChanged('additional.metadata')}
                    >
                        {metadata.length > 0 &&
                            metadata.map(([key, { value, inherited }]) => (
                                <Fragment key={key}>
                                    <div className={cln('key', { highlighted: isMetadataKeyChanged(key) })}>
                                        {key}:{inherited && <span className={css.inherited}>inherited</span>}
                                    </div>
                                    <div className={css.value}>
                                        <CopyToClipboard text={value} className={css.copy} />
                                    </div>
                                </Fragment>
                            ))}
                    </FieldContainer>
                </div>
                <div className={css.additional}>
                    <FieldContainer label="Poster image" highlighted={isFieldChanged('additional.image')}>
                        {additional.image && <img src={additional.image} alt="Poster preview" className={css.image} />}
                    </FieldContainer>
                    <FieldContainer
                        label="Additional images"
                        fieldClassName={css.imagesList}
                        highlighted={isFieldChanged('additional.images')}
                    >
                        {images.length > 0 &&
                            images.map(([key, url]) => (
                                <div key={key} className={css.item}>
                                    <span className={css.inlineLabel}>{key}</span>
                                    <img src={url} alt={`${key} preview`} className={css.image} />
                                </div>
                            ))}
                    </FieldContainer>
                    <FieldContainer label="Logo" highlighted={isFieldChanged('additional.logo')}>
                        {additional.logo && <img src={additional.logo} alt="Logo preview" className={css.image} />}
                    </FieldContainer>
                    <FieldContainer label="Background" highlighted={isFieldChanged('additional.background')}>
                        {additional.background && (
                            <img src={additional.background} alt="Background preview" className={css.image} />
                        )}
                    </FieldContainer>
                    <FieldContainer
                        label="Background colour"
                        highlighted={isFieldChanged('additional.backgroundColor')}
                    >
                        {additional.backgroundColor && (
                            <>
                                <span
                                    className={css.colorSwatch}
                                    style={{ backgroundColor: additional.backgroundColor }}
                                />
                                {additional.backgroundColor}
                            </>
                        )}
                    </FieldContainer>
                    <FieldContainer
                        label="Player logo"
                        highlighted={isFieldChanged(['additional.playerLogoImage', 'additional.showPlayerLogo'])}
                    >
                        {additional.showPlayerLogo && additional.playerLogoImage ? (
                            <img src={additional.playerLogoImage} alt="Player logo preview" className={css.image} />
                        ) : (
                            formatBoolean(additional.showPlayerLogo)
                        )}
                    </FieldContainer>

                    <Divider margin="0" />

                    <FieldContainer
                        label="Tracking code"
                        highlighted={isFieldChanged('additional.trackingCode')}
                        copyable
                    >
                        {additional.trackingCode}
                    </FieldContainer>
                    <FieldContainer
                        label="Ad campaign id"
                        highlighted={isFieldChanged('additional.adCampaignId')}
                        copyable
                    >
                        {additional.adCampaignId}
                    </FieldContainer>
                    <FieldContainer
                        label="Autoplay next asset"
                        highlighted={isFieldChanged('additional.autoPlayNextAsset')}
                    >
                        {formatBoolean(additional.autoPlayNextAsset)}
                    </FieldContainer>
                    <FieldContainer
                        label="Autoplay next embed"
                        highlighted={isFieldChanged('additional.autoPlayNextEmbed')}
                    >
                        {formatBoolean(additional.autoPlayNextEmbed)}
                    </FieldContainer>
                    <FieldContainer label="End poster" highlighted={isFieldChanged('additional.showEndPoster')}>
                        {formatBoolean(additional.showEndPoster)}
                    </FieldContainer>
                    <FieldContainer label="Comments" highlighted={isFieldChanged('additional.showComments')}>
                        {formatBoolean(additional.showComments)}
                    </FieldContainer>
                    <FieldContainer label="All devices" highlighted={isFieldChanged('additional.allowAllDevices')}>
                        {formatBoolean(additional.allowAllDevices)}
                    </FieldContainer>
                    <Divider margin="0" />

                    <FieldContainer label="Modified">{formatEditedDate(date)}</FieldContainer>
                </div>
            </Dialog.Section>
            <Dialog.Section mode="flexRight" variant="darker">
                <Button type="button" variant="standard" onClick={closeDialog}>
                    Cancel
                </Button>
            </Dialog.Section>
        </Dialog>
    );
}
